import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Space, Switch } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { StyledDrawer, StyledInput, StyledFormSelect, FormSection, PrimaryButton, CancelButton, ErrorText, FieldLabel } from './styles';
import GoogleMapsAddressInput from './GoogleMapsAddressInput';
import StoreImageUploadField from './StoreImageUploadField';
import type { StoreFormProps, StoreFormSubmitPayload } from './types';
import type { AddressErrors } from './GoogleMapsAddressInput/types';
import { LABELS, VALIDATION_MESSAGES, PLACEHOLDERS, MESSAGES, toSafeImageUrl } from './constants';
import { useMerchantsList } from './hooks';
import type { RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';

type FormValues = {
  name: string;
  merchantId?: string;
  estimatedPreparationTime?: string;
  autoAcceptOrders: boolean;
  taxable: boolean;
  taxRate: string;
};

const StoreForm: React.FC<StoreFormProps> = ({ store, loading, onSubmit, onCancel }) => {
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const role = useSelector((state: RootState) => state.auth.role);
  const { isDarkMode } = useTheme();

  const [imageFile, setImageFile] = useState<File | null>(null);
  // true once the user explicitly removes the existing image — distinct
  // from "no file chosen yet" so we know to send `imageUrl: null` on submit.
  const [imageRemoved, setImageRemoved] = useState(false);

  // Backend only accepts merchantId from an admin on CREATE — PATCH /stores/:id
  // never reads it from the body (merchant is always derived from the JWT
  // there), so the picker only applies when creating a new store as admin.
  const isAdmin = role === 'admin';
  const showMerchantPicker = isAdmin && !store;
  const { data: merchants = [], isLoading: merchantsLoading } = useMerchantsList(showMerchantPicker);

  const [currentStoreData, setCurrentStoreData] = useState(() => ({
    address: store?.address || '',
    streetName: store?.streetName || '',
    streetNumber: store?.streetNumber || '',
    apartment: store?.apartment || '',
    city: store?.city || '',
    state: store?.state || '',
    zipCode: store?.zipCode || '',
    phoneNumber: store?.phoneNumber || '',
    latitude: Number(store?.latitude) || 0,
    longitude: Number(store?.longitude) || 0,
  }));

  const { control, handleSubmit, formState: { errors }, reset, watch, trigger } = useForm<FormValues>({
    defaultValues: { name: '', merchantId: undefined, estimatedPreparationTime: '', autoAcceptOrders: false, taxable: false, taxRate: '' },
  });
  const taxable = watch('taxable');

  useEffect(() => {
    setImageFile(null);
    setImageRemoved(false);
    if (store) {
      reset({
        name: store.name || '',
        merchantId: store.merchant?.id || undefined,
        estimatedPreparationTime: store.estimatedPreparationTime || '',
        autoAcceptOrders: store.autoAcceptOrders ?? false,
        taxable: store.taxable ?? false,
        // Store rate is a fraction (0.08875); the form edits it as a
        // percentage (8.875), matching how the value is displayed.
        taxRate: store.taxRate != null && !isNaN(Number(store.taxRate)) && Number(store.taxRate) > 0
          ? String(Number(store.taxRate) * 100)
          : '',
      });
      setCurrentStoreData({
        address: store.address || '',
        streetName: store.streetName || '',
        streetNumber: store.streetNumber || '',
        apartment: store.apartment || '',
        city: store.city || '',
        state: store.state || '',
        zipCode: store.zipCode || '',
        phoneNumber: store.phoneNumber || '',
        latitude: Number(store.latitude) || 0,
        longitude: Number(store.longitude) || 0,
      });
    } else {
      reset({ name: '', merchantId: undefined, estimatedPreparationTime: '', autoAcceptOrders: false, taxable: false, taxRate: '' });
      setCurrentStoreData({
        address: '',
        streetName: '',
        streetNumber: '',
        apartment: '',
        city: '',
        state: '',
        zipCode: '',
        phoneNumber: '',
        latitude: 0,
        longitude: 0,
      });
    }
  }, [store, reset]);

  useEffect(() => {
    setAddressErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if (currentStoreData.streetNumber?.trim()) delete next.streetNumber;
      if (currentStoreData.streetName?.trim()) delete next.streetName;
      if (currentStoreData.apartment?.trim()) delete next.apartment;
      if (currentStoreData.city?.trim()) delete next.city;
      if (currentStoreData.state?.trim()) delete next.state;
      if (currentStoreData.zipCode?.trim()) delete next.zipCode;
      if (currentStoreData.phoneNumber?.trim()) delete next.phoneNumber;
      if (currentStoreData.address?.trim()) delete next.address;
      return next;
    });
  }, [currentStoreData]);

  const onFormSubmit = (values: FormValues) => {
    // Mirrors the backend's CreateStoreDto: name, apartment, address,
    // streetNumber, streetName, city, state, zipCode and phoneNumber are all
    // `@IsNotEmpty()` — every one of them 400s if blank.
    const addrErrors: AddressErrors = {};
    if (!currentStoreData.streetNumber?.trim()) addrErrors.streetNumber = 'Street Number is required';
    if (!currentStoreData.streetName?.trim()) addrErrors.streetName = 'Street Name is required';
    if (!currentStoreData.apartment?.trim()) addrErrors.apartment = 'Apt/Unit is required';
    if (!currentStoreData.city?.trim()) addrErrors.city = 'City is required';
    if (!currentStoreData.state?.trim()) addrErrors.state = 'State is required';
    if (!currentStoreData.zipCode?.trim()) addrErrors.zipCode = 'ZIP Code is required';
    if (!currentStoreData.phoneNumber?.trim()) addrErrors.phoneNumber = 'Phone Number is required';
    if (!currentStoreData.address?.trim()) addrErrors.address = 'Full Address is required';

    if (Object.keys(addrErrors).length > 0) {
      setAddressErrors(addrErrors);
      return;
    }

    setAddressErrors({});
    const payload: StoreFormSubmitPayload = {
      ...(store ? { id: store.id } : {}),
      name: values.name.trim(),
      address: currentStoreData.address?.trim() || '',
      city: currentStoreData.city?.trim() || '',
      phoneNumber: currentStoreData.phoneNumber?.trim() || '',
      streetName: currentStoreData.streetName?.trim() || '',
      streetNumber: currentStoreData.streetNumber?.trim() || '',
      apartment: currentStoreData.apartment?.trim() || '',
      state: currentStoreData.state?.trim() || '',
      zipCode: currentStoreData.zipCode?.trim() || '',
      latitude: currentStoreData.latitude || 0,
      longitude: currentStoreData.longitude || 0,
      ...(values.estimatedPreparationTime?.trim()
        ? { estimatedPreparationTime: Number(values.estimatedPreparationTime) }
        : {}),
      autoAcceptOrders: values.autoAcceptOrders,
      taxable: values.taxable,
      // Form edits the rate as a percentage; backend wants the fraction.
      taxRate: values.taxRate?.trim() ? Number(values.taxRate) / 100 : 0,
      // Backend requires merchantId in the body only when an admin creates a
      // store (BadRequestException otherwise); for merchant/merchant_manager
      // roles it's derived from the JWT and ignored if sent.
      ...(showMerchantPicker ? { merchantId: values.merchantId } : {}),
      // A new file switches the mutation to a multipart request; otherwise
      // an explicit removal sends `imageUrl: null` over plain JSON, and an
      // untouched existing image is resent as-is so update doesn't drop it.
      ...(imageFile
        ? { imageFile }
        : imageRemoved
        ? { imageUrl: null }
        : toSafeImageUrl(store?.imageUrl)
        ? { imageUrl: toSafeImageUrl(store?.imageUrl) }
        : {}),
    };
    onSubmit(payload);
  };

  return (
    <StyledDrawer
      title={
        <Space>
          <span>🏪</span>
          <span>{store ? LABELS.UPDATE_STORE : LABELS.CREATE_STORE}</span>
        </Space>
      }
      placement="right"
      open={true}
      onClose={onCancel}
      maskClosable={false}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CancelButton key="cancel" onClick={onCancel} disabled={loading} style={{ height: 40, paddingInline: 24, marginLeft: 0 }}>
            {LABELS.CANCEL}
          </CancelButton>
          <PrimaryButton
            key="submit"
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleSubmit(onFormSubmit)}
            loading={loading}
            style={{ height: 40, paddingInline: 24 }}
          >
            {store ? LABELS.UPDATE : LABELS.CREATE}
          </PrimaryButton>
        </div>
      }
      width={760}
      destroyOnClose
    >
      <FormSection>
        <div>
          <FieldLabel>{LABELS.STORE_IMAGE}</FieldLabel>
          <StoreImageUploadField
            imageFile={imageFile}
            existingImageUrl={imageRemoved ? '' : toSafeImageUrl(store?.imageUrl)}
            isDarkMode={isDarkMode}
            onFileSelected={(file) => {
              setImageFile(file);
              setImageRemoved(false);
            }}
            onDelete={() => {
              setImageFile(null);
              setImageRemoved(true);
            }}
          />
        </div>

        {showMerchantPicker && (
          <div>
            <FieldLabel>{LABELS.MERCHANT} <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            <Controller
              name="merchantId"
              control={control}
              rules={{ required: VALIDATION_MESSAGES.MERCHANT_REQUIRED }}
              render={({ field }) => (
                <StyledFormSelect
                  {...field}
                  value={field.value || undefined}
                  showSearch
                  loading={merchantsLoading}
                  placeholder={PLACEHOLDERS.SELECT_MERCHANT}
                  status={errors.merchantId ? 'error' : ''}
                  options={merchants.map((m) => ({ value: m.id, label: m.name }))}
                  filterOption={(input: string, option?: { label: string; value: string }) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            />
            {errors.merchantId && <ErrorText>{errors.merchantId.message}</ErrorText>}
          </div>
        )}

        <div>
          <FieldLabel>{LABELS.STORE_NAME} <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
          <Controller
            name="name"
            control={control}
            rules={{ required: VALIDATION_MESSAGES.STORE_NAME_REQUIRED }}
            render={({ field }) => (
              <StyledInput
                {...field}
                placeholder={PLACEHOLDERS.STORE_NAME}
                status={errors.name ? 'error' : ''}
              />
            )}
          />
          {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
        </div>

        <div>
          <FieldLabel>{LABELS.PREP_TIME}</FieldLabel>
          <Controller
            name="estimatedPreparationTime"
            control={control}
            rules={{
              validate: (value) =>
                !value || (Number(value) >= 0 && Number.isFinite(Number(value)))
                  ? true
                  : VALIDATION_MESSAGES.PREP_TIME_INVALID,
            }}
            render={({ field }) => (
              <StyledInput
                {...field}
                type="number"
                min={0}
                placeholder={PLACEHOLDERS.PREP_TIME}
                status={errors.estimatedPreparationTime ? 'error' : ''}
              />
            )}
          />
          {errors.estimatedPreparationTime && (
            <ErrorText>{errors.estimatedPreparationTime.message}</ErrorText>
          )}
        </div>

        <div>
          <FieldLabel style={{ marginBottom: 0 }}>{LABELS.AUTO_ACCEPT_ORDERS}</FieldLabel>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>{MESSAGES.AUTO_ACCEPT_HELPER}</div>
          <Controller
            name="autoAcceptOrders"
            control={control}
            render={({ field }) => (
              <Switch checked={field.value} onChange={field.onChange} />
            )}
          />
        </div>

        <div>
          <FieldLabel style={{ marginBottom: 0 }}>{LABELS.STORE_TAXABLE}</FieldLabel>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>{MESSAGES.TAXABLE_HELPER}</div>
          <Controller
            name="taxable"
            control={control}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onChange={(checked) => {
                  field.onChange(checked);
                  // Re-validate immediately so a stale "required when
                  // taxable" message on the rate field below doesn't linger
                  // after tax is turned off.
                  trigger('taxRate');
                }}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel>
            {LABELS.STORE_TAX_RATE} {taxable && <span style={{ color: '#ff4d4f' }}>*</span>}
          </FieldLabel>
          <Controller
            name="taxRate"
            control={control}
            rules={{
              validate: (value) => {
                const num = value?.trim() ? Number(value) : 0;
                if (!Number.isFinite(num) || num < 0 || num > 100) return VALIDATION_MESSAGES.TAX_RATE_INVALID;
                if (taxable && num <= 0) return VALIDATION_MESSAGES.TAX_RATE_REQUIRED_WHEN_TAXABLE;
                const decimals = value?.trim().split('.')[1];
                if (decimals && decimals.length > 3) return VALIDATION_MESSAGES.TAX_RATE_TOO_PRECISE;
                return true;
              },
            }}
            render={({ field }) => (
              <StyledInput
                value={field.value}
                onChange={(e) => {
                  // Kept exactly as typed (even out of range or
                  // over-precise) so the validate rule above can catch it
                  // and show a message, instead of silently correcting or
                  // rounding bad input away.
                  field.onChange(e.target.value);
                  trigger('taxRate');
                }}
                onBlur={() => {
                  field.onBlur();
                  trigger('taxRate');
                }}
                type="number"
                step={0.001}
                placeholder={PLACEHOLDERS.TAX_RATE}
                status={errors.taxRate ? 'error' : ''}
              />
            )}
          />
          {errors.taxRate && <ErrorText>{errors.taxRate.message}</ErrorText>}
        </div>

        <div>
          <FieldLabel>{LABELS.STORE_ADDRESS} <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
          <GoogleMapsAddressInput
            currentStore={currentStoreData}
            setCurrentStore={setCurrentStoreData}
            addressErrors={addressErrors}
          />
        </div>

      </FormSection>
    </StyledDrawer>
  );
};

export default React.memo(StoreForm);
